import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import useAnimalHandlingAlertContext from "../../../hooks/useAnimalHandlingAlertContext";

import { Dropdown, Menu, notification, Table } from "antd";
import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import TagStatus from "../../../components/utils/tagStatus";

import { deleteAlert } from "../../../services/animalHandlingAlertService";
import { getColumnSearchProps } from "../../../utils/tableUtils";

const AnimalHandlingAlertTable = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { dataList, isLoadingDataList, openEditDrawer, fetchData } =
    useAnimalHandlingAlertContext();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = useCallback((selectedKeys, confirm, dataIndex) => {
    confirm();
  }, []);
  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleEdit = useCallback(
    (record) => {
      openEditDrawer(record);
    },
    [openEditDrawer],
  );

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteAlert({ groupId, farmId, id });
        notification.success({
          message:
            translation.animalHandlingAlert.form.notifications.deleteSuccess,
        });
        fetchData();
      } catch (error) {
        notification.error({
          message:
            translation.animalHandlingAlert.form.notifications.deleteError,
        });
      }
    },
    [farmId, fetchData, groupId, translation],
  );

  const menu = useCallback(
    (record) => (
      <Menu>
        {record.status === "ACTIVE" && (
          <Menu.Item key="1" onClick={() => handleEdit(record)}>
            {translation.table.menu.edit}
          </Menu.Item>
        )}
        {record.status === "ACTIVE" && (
          <Menu.Item key="2" onClick={() => handleDelete(record.id)}>
            {translation.table.menu.delete}
          </Menu.Item>
        )}
      </Menu>
    ),
    [handleEdit, handleDelete, translation],
  );

  const renderVisualizationType = useCallback(
    (value) => {
      const { permanent, confirm, expiration } =
        translation.animalHandlingAlert.form.visualizationType;
      const map = {
        PERMANENT: permanent,
        CONFIRM: confirm,
        EXPIRATION: expiration,
      };
      return map[value] || value;
    },
    [translation],
  );

  return (
    <Table
      loading={isLoadingDataList}
      rowKey="id"
      size="small"
      dataSource={dataList}
      scroll={{ x: true }}
      pagination={{
        hideOnSinglePage:
          dataList !== null && dataList.length > 10 ? false : true,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
      }}
    >
      <Table.Column
        title={translation.animalHandlingAlert.table.columns.status}
        dataIndex="status"
        key="status"
        align="left"
        defaultFilteredValue={["ACTIVE"]}
        filtered
        filters={[
          { text: translation.status.active, value: "ACTIVE" },
          { text: translation.status.confirmed, value: "CONFIRMED" },
        ]}
        filterMultiple
        onFilter={(value, record) => record.status.includes(value)}
        render={(status) =>
          status === "ACTIVE" ? (
            <TagStatus
              background="#C5F1CA"
              borderColor="#106518"
              color="#106518"
            >
              {translation.status.active}
            </TagStatus>
          ) : status === "CONFIRMED" ? (
            <TagStatus
              background="#FCE9A8"
              borderColor="#D4A41B"
              color="#D4A41B"
            >
              {translation.status.confirmed}
            </TagStatus>
          ) : (
            <TagStatus
              background="#FBC7C7"
              borderColor="#D44C4C"
              color="#D44C4C"
            >
              {translation.status.inactive}
            </TagStatus>
          )
        }
      />
      <Table.Column
        title={translation.animalHandlingAlert.table.columns.handlingNumber}
        dataIndex="handlingNumber"
        key="handlingNumber"
        filtered
        {...getColumnSearchProps("handlingNumber", handleReset, handleSearch)}
        onFilter={(value, record) => record.handlingNumber.includes(value)}
      />
      <Table.Column
        title={translation.animalHandlingAlert.table.columns.visualizationType}
        dataIndex="visualizationType"
        key="visualizationType"
        filtered
        filters={[
          {
            text: translation.animalHandlingAlert.form.visualizationType
              .permanent,
            value: "PERMANENT",
          },
          {
            text: translation.animalHandlingAlert.form.visualizationType
              .confirm,
            value: "CONFIRM",
          },
          {
            text: translation.animalHandlingAlert.form.visualizationType
              .expiration,
            value: "EXPIRATION",
          },
        ]}
        filterMultiple
        onFilter={(value, record) => record.visualizationType.includes(value)}
        render={renderVisualizationType}
      />
      <Table.Column
        title={translation.animalHandlingAlert.table.columns.message}
        dataIndex="message"
        key="message"
        render={(text) =>
          text && text.length > 100 ? `${text.substring(0, 100)}...` : text
        }
      />
      <Table.Column
        title=""
        align="left"
        render={(text, record) => (
          <Dropdown overlay={menu(record)} trigger={["click"]} key={record.id}>
            <MenuContainer>
              <MenuIcon />
            </MenuContainer>
          </Dropdown>
        )}
      />
    </Table>
  );
};

export default AnimalHandlingAlertTable;
